/**
 * Bulk-add people from a spreadsheet.
 * @file src/web/features/team/components/ImportPeopleDialog.tsx
 *
 * @llm-rule WHEN: A Company Admin or Head onboards more people than is sensible one at a time
 * @llm-rule NOTE: The workbook is parsed HERE, in the browser, and the server
 *   receives plain JSON rows. That keeps multipart handling, an upload
 *   directory and temp-file cleanup out of the API entirely.
 * @llm-rule AVOID: Importing exceljs at module scope — it is roughly a megabyte
 *   and nothing else on the People page needs it. It is loaded on demand.
 */

import { useState, type ChangeEvent } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  PasswordInput,
  toast,
} from '@bloomneo/uikit';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useAppRequest } from '../../../shared/hooks/useAppApi';

/** Columns the sheet is expected to have, in order. */
const COLUMNS = ['Name', 'Email', 'Role', 'Title', 'Phone'] as const;

/** Roles a sheet may name. Company Admin is deliberately not importable. */
const IMPORTABLE_ROLES = ['EMPLOYEE', 'MANAGER', 'HEAD'];

interface ParsedRow {
  name: string;
  email: string;
  orgRole: string;
  title?: string;
  phone?: string;
}

interface RowResult {
  row: number;
  email: string;
  status: 'created' | 'skipped' | 'failed';
  reason?: string;
}

export function ImportPeopleDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}) {
  const { request } = useAppRequest();

  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [password, setPassword] = useState('');
  const [start, setStart] = useState('10:00');
  const [end, setEnd] = useState('18:00');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<RowResult[] | null>(null);

  function reset() {
    setRows([]);
    setFileName('');
    setPassword('');
    setResults(null);
  }

  /** A blank sheet with the right headers and one example row. */
  async function downloadSample() {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const sheet = wb.addWorksheet('People');

      sheet.columns = COLUMNS.map((header) => ({
        header,
        key: header.toLowerCase(),
        width: header === 'Email' ? 32 : 20,
      }));
      sheet.getRow(1).font = { bold: true };

      sheet.addRow(['Arun Kumar', 'arun@yourcompany.com', 'EMPLOYEE', 'Engineer', '9876543210']);
      sheet.addRow(['Meera Rao', 'meera@yourcompany.com', 'MANAGER', 'Team Manager', '']);

      // A note row so whoever opens this knows what the Role column accepts.
      sheet.addRow([]);
      sheet.addRow([`Role must be one of: ${IMPORTABLE_ROLES.join(', ')}`]);
      sheet.addRow(['Title and Phone are optional. Delete these note rows before importing.']);

      const buffer = await wb.xlsx.writeBuffer();
      const url = URL.createObjectURL(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = 'presynce-people-sample.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not build the sample file');
    }
  }

  async function pickFile(file: File) {
    setResults(null);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(await file.arrayBuffer());

      const sheet = wb.worksheets[0];
      if (!sheet) return toast.error('That workbook has no sheets');

      const parsed: ParsedRow[] = [];
      sheet.eachRow((row, index) => {
        if (index === 1) return; // header

        const cell = (n: number) => String(row.getCell(n).text ?? '').trim();
        const name = cell(1);
        const email = cell(2);

        // Blank lines and the sample's own note rows carry no email — skipping
        // them quietly is kinder than reporting five failures per file.
        if (!name && !email) return;
        if (!email) return;

        parsed.push({
          name,
          email,
          orgRole: (cell(3) || 'EMPLOYEE').toUpperCase(),
          title: cell(4) || undefined,
          phone: cell(5) || undefined,
        });
      });

      if (parsed.length === 0) {
        return toast.error('No rows with an email address were found');
      }

      setRows(parsed);
      setFileName(file.name);
      toast.success(`${parsed.length} row${parsed.length === 1 ? '' : 's'} read`);
    } catch {
      toast.error('That file could not be read as a spreadsheet');
    }
  }

  async function submit() {
    if (rows.length === 0) return toast.error('Choose a file first');
    if (password.trim().length < 8) return toast.error('Set a shared password of at least 8 characters');
    if (end <= start) return toast.error('The end of the day must be after the start');

    setBusy(true);
    try {
      const result = await request<{ message: string; results: RowResult[] }>(
        '/api/org/people/import',
        {
          method: 'POST',
          body: { people: rows, password: password.trim(), workStartTime: start, workEndTime: end },
        },
      );

      if (!result.ok) return toast.error(result.error);

      setResults(result.data.results);
      toast.success(result.data.message);
      onImported();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] w-[calc(100vw-1.5rem)] max-w-lg overflow-y-auto sm:w-full">
        <DialogHeader>
          <DialogTitle>Add people from a spreadsheet</DialogTitle>
          <DialogDescription>
            Everyone in the file gets the same starting password and the same working day. Change
            either for an individual afterwards.
          </DialogDescription>
        </DialogHeader>

        {results ? (
          <div className="flex flex-col gap-2">
            <div className="max-h-[40vh] overflow-y-auto rounded-[10px] border border-line">
              {results.map((r) => (
                <div
                  key={`${r.row}-${r.email}`}
                  className="flex items-baseline justify-between gap-3 border-b border-line px-3 py-2 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[12.5px] text-ink">{r.email}</div>
                    {r.reason && (
                      <div className="truncate text-[11px] text-ink-ghost">{r.reason}</div>
                    )}
                  </div>
                  <span
                    className={`flex-none text-[11px] font-medium ${
                      r.status === 'created'
                        ? 'text-ink'
                        : r.status === 'skipped'
                          ? 'text-ink-ghost'
                          : 'text-flag'
                    }`}
                  >
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-ink-soft">
              Everyone added signs in with the password you set. Ask them to change it.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-[10px] border border-dashed border-rule p-3">
              <div className="flex items-center gap-2 text-[12.5px] text-ink-soft">
                <FileSpreadsheet className="size-4 flex-none text-ink-faint" strokeWidth={1.8} />
                <span>Columns: {COLUMNS.join(' · ')}</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="mt-1.5 h-7 px-2"
                onClick={() => void downloadSample()}
              >
                <Download className="mr-1.5 size-3" />
                Download sample .xlsx
              </Button>
            </div>

            <FormField label="Spreadsheet" required>
              <input
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void pickFile(file);
                }}
                className="w-full text-[12.5px] text-ink-soft file:mr-3 file:rounded-[8px] file:border file:border-line file:bg-paper file:px-3 file:py-1.5 file:text-[12.5px] file:text-ink"
              />
            </FormField>

            {rows.length > 0 && (
              <p className="text-[12px] text-ink-soft">
                <strong className="text-ink">{rows.length}</strong> people read from {fileName}.
              </p>
            )}

            <FormField
              label="Shared password"
              required
              helper="Everyone in this file starts with it, then changes it."
            >
              <PasswordInput
                value={password}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Day starts" required>
                <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
              </FormField>
              <FormField label="Day ends" required>
                <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              </FormField>
            </div>
          </div>
        )}

        <DialogFooter>
          {results ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} disabled={busy || rows.length === 0}>
                <Upload className="mr-1.5 size-3.5" />
                {busy ? 'Adding…' : `Add ${rows.length || ''} people`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
